/* assets/src/viewer/models/awnings.js
 * Modelli TENDA (port fedele three-scene.js:1942-2659): a bracci, 8000 EVO, cappottina, molla/fall.
 * Architettura F6: materiali persistenti (cross-fade colore), il gruppo è l'activeFabricGroup
 * con userData.updateLayout (animazione apri/chiudi). update(dims) ricostruisce il sotto-albero
 * UNA volta per cambio discreto (l'effetto "grow" fluido è gestito dal morph nel controller,
 * non per-frame), senza dispose del gruppo radice né reframe camera.
 */
import * as THREE from 'three';

const H = 2.4;

function applyShadows(mesh) { mesh.castShadow = true; mesh.receiveShadow = true; return mesh; }

function extrudedAlongX(shape, span, material, bevelSize = 0.002) {
  const geo = new THREE.ExtrudeGeometry(shape, { depth: span, bevelEnabled: true, bevelSegments: 2, steps: 1, bevelSize, bevelThickness: bevelSize });
  geo.translate(0, 0, -span / 2);
  geo.rotateY(Math.PI / 2);
  return applyShadows(new THREE.Mesh(geo, material));
}

function alignBeam(mesh, start, end) {
  const delta = new THREE.Vector3().subVectors(end, start);
  const length = Math.max(0.001, delta.length());
  mesh.position.copy(start).add(end).multiplyScalar(0.5);
  mesh.scale.set(1, 1, length);
  mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), delta.clone().normalize());
}

function linkBeam(widthX, thicknessY, material) { return applyShadows(new THREE.Mesh(new THREE.BoxGeometry(widthX, thicknessY, 1), material)); }
function pinDisc(radius, thickness, material, axis = 'x') {
  const mesh = applyShadows(new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, thickness, 24), material));
  if (axis === 'x') mesh.rotation.z = Math.PI / 2; else mesh.rotation.x = Math.PI / 2;
  return mesh;
}

function solveElbow(start, end, l1, l2) {
  const deltaZ = end.z - start.z; const deltaY = end.y - start.y;
  let dist = Math.sqrt(deltaZ * deltaZ + deltaY * deltaY);
  const maxReach = Math.max(0.05, l1 + l2 - 0.001);
  const minReach = Math.max(0.04, Math.abs(l1 - l2) + 0.001);
  let targetY = end.y; let targetZ = end.z;
  if (dist > maxReach && dist > 0.0001) { const r = maxReach / dist; targetY = start.y + deltaY * r; targetZ = start.z + deltaZ * r; dist = maxReach; }
  else if (dist < minReach && dist > 0.0001) { const r = minReach / dist; targetY = start.y + deltaY * r; targetZ = start.z + deltaZ * r; dist = minReach; }
  const a = (l1 * l1 - l2 * l2 + dist * dist) / (2 * dist);
  const hVal = Math.sqrt(Math.max(0, l1 * l1 - a * a));
  const midZ = start.z + (targetZ - start.z) * (a / dist);
  const midY = start.y + (targetY - start.y) * (a / dist);
  const perpZ = -(targetY - start.y) / dist; const perpY = (targetZ - start.z) / dist;
  const eA = new THREE.Vector3(start.x, midY + perpY * hVal, midZ + perpZ * hVal);
  const eB = new THREE.Vector3(start.x, midY - perpY * hVal, midZ - perpZ * hVal);
  return eA.y < eB.y ? eA : eB;
}

// Base comune: gestione materiali persistenti + clear/rebuild.
class AwningBase {
  constructor(materials, colors) {
    this.materials = materials;
    this.group = new THREE.Group();
    this.fabricGroup = this.group;
    this.matStruttura = materials.structureMaterial(colors.structColor);
    this.foldMat = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color(colors.fabricColor),
      map: materials.textures.fabric ? materials.textures.fabric.map : null,
      bumpMap: materials.textures.fabric ? materials.textures.fabric.bump : null,
      bumpScale: 0.013, roughness: 0.9, metalness: 0.02, side: THREE.DoubleSide,
    });
    this.foldMat.userData.asgRole = 'fabric';
    this.detailMat = new THREE.MeshStandardMaterial({ color: 0xe2e8f0, roughness: 0.42, metalness: 0.28 });
    this.darkPinMat = new THREE.MeshStandardMaterial({ color: 0x475569, roughness: 0.5, metalness: 0.5 });
  }

  _clear() {
    this.group.traverse((o) => { if (o.geometry && o.geometry.dispose) o.geometry.dispose(); });
    for (let i = this.group.children.length - 1; i >= 0; i--) this.group.remove(this.group.children[i]);
    this.group.scale.set(1, 1, 1);
  }

  // Parete di fondo (intonaco): àncora le tende a muro (niente più "fluttuare") e ne riceve l'ombra.
  _wall(w, hRear, zBack) {
    const wh = hRear + 1.4;
    const mat = new THREE.MeshStandardMaterial({ color: 0xe7e1d6, roughness: 0.96, metalness: 0.0 });
    const wall = new THREE.Mesh(new THREE.PlaneGeometry(w + 2.4, wh), mat);
    wall.position.set(0, wh / 2 - 0.25, zBack);
    wall.receiveShadow = true;
    wall.userData.asgRole = 'wall';
    this.group.add(wall);
  }

  build(dims, config) { this._construct(dims, config || {}); }
  update(dims, config) { this._clear(); this._construct(dims, config || this._cfg || {}); this._relayout(); }
  _relayout() {
    const p = typeof window.ASG_TeloProgress === 'number' ? window.ASG_TeloProgress : 0;
    if (this.group.userData.updateLayout) this.group.userData.updateLayout(p);
  }
  setStructureColor(c) { this.materials.crossfadeColor(this.matStruttura, c); }
  setFabricColor(c) { this.materials.crossfadeColor(this.foldMat, c); }
  updateLayout(prog) { if (this.group.userData.updateLayout) this.group.userData.updateLayout(prog); }
  dispose() {
    this._clear();
    [this.matStruttura, this.foldMat, this.detailMat, this.darkPinMat].forEach((m) => m && m.dispose());
  }
}

// ---------- TENDA A BRACCI (awning_arm) ----------
export class AwningArmModel extends AwningBase {
  constructor(m, c) { super(m, c); this.type = 'awning_arm'; }
  _construct(dims, config) {
    this._cfg = config;
    const w = dims.w; const d = dims.d;
    const wallLineZ = -d / 2 + 0.06;
    const usableWidth = Math.max(0.75, w - 0.12);
    const projection = Math.max(0.70, d - 0.10);
    const matStruttura = this.matStruttura; const detailMat = this.detailMat; const darkPinMat = this.darkPinMat;
    const g = this.group;
    this._wall(w, H + 0.50, wallLineZ - 0.03);

    const squareBarY = (H + 0.50) - 0.10;
    const squareBarZ = wallLineZ + 0.05;
    const sqBar = applyShadows(new THREE.Mesh(new THREE.BoxGeometry(w, 0.05, 0.05), matStruttura));
    sqBar.position.set(0, squareBarY, squareBarZ); g.add(sqBar);

    const numBrackets = w > 6.0 ? 5 : (w > 4.0 ? 4 : (w > 2.5 ? 3 : 2));
    for (let i = 0; i < numBrackets; i++) {
      const bx = -w / 2 + 0.2 + (w - 0.4) * (i / Math.max(1, numBrackets - 1));
      const bracket = applyShadows(new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.24, 0.15), matStruttura));
      bracket.position.set(bx, squareBarY + 0.03, wallLineZ + 0.075); g.add(bracket);
    }

    const rollerY = squareBarY + 0.07; const rollerZ = squareBarZ + 0.05;
    const rollerGeo = new THREE.CylinderGeometry(0.045, 0.045, usableWidth + 0.06, 24); rollerGeo.rotateZ(Math.PI / 2);
    const roller = applyShadows(new THREE.Mesh(rollerGeo, matStruttura)); roller.position.set(0, rollerY, rollerZ); g.add(roller);

    const fabricGeo = new THREE.PlaneGeometry(usableWidth, 1, 24, 8); fabricGeo.translate(0, -0.5, 0);
    const fabricMesh = applyShadows(new THREE.Mesh(fabricGeo, this.foldMat)); g.add(fabricMesh);

    const terminalShape = new THREE.Shape();
    terminalShape.moveTo(0, 0.06);
    terminalShape.bezierCurveTo(-0.03, 0.06, -0.05, 0.04, -0.07, 0);
    terminalShape.bezierCurveTo(-0.05, -0.04, -0.03, -0.06, 0, -0.06);
    terminalShape.lineTo(0, 0.06); terminalShape.closePath();
    const terminalBar = extrudedAlongX(terminalShape, usableWidth + 0.04, matStruttura, 0.002); g.add(terminalBar);

    const frontCaps = [];
    [-1, 1].forEach((dir) => {
      const cap = applyShadows(new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.12, 0.08), detailMat));
      cap.userData.dir = dir; frontCaps.push(cap); g.add(cap);
    });

    let numArms = 2; if (usableWidth >= 4.5) numArms = 3; if (usableWidth >= 6.5) numArms = 4;
    const armAssemblies = [];
    const startX = -usableWidth * 0.42; const endX = usableWidth * 0.42;
    const spacing = numArms > 1 ? (endX - startX) / (numArms - 1) : 0;
    for (let i = 0; i < numArms; i++) {
      const armX = startX + i * spacing;
      const foldSign = (i < numArms / 2) ? 1 : -1;
      const shoulderBracket = applyShadows(new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.14, 0.09), matStruttura));
      shoulderBracket.position.set(armX, squareBarY, squareBarZ + 0.025); g.add(shoulderBracket);
      const upperArm = linkBeam(0.09, 0.05, detailMat);
      const lowerArm = linkBeam(0.08, 0.045, detailMat);
      const elbowPin = pinDisc(0.045, 0.07, darkPinMat, 'z');
      g.add(upperArm, lowerArm, elbowPin);
      armAssemblies.push({ armX, upperArm, lowerArm, elbowPin, foldSign });
    }

    g.userData.updateLayout = (prog) => {
      const openness = 1 - THREE.MathUtils.clamp(prog, 0, 1);
      const slopeAngle = THREE.MathUtils.degToRad(15);
      const minExt = 0.20;
      const currentExt = minExt + openness * Math.max(0.12, projection - minExt);
      const terminalY = rollerY - Math.sin(slopeAngle) * currentExt;
      const terminalZ = rollerZ + Math.cos(slopeAngle) * currentExt;
      fabricMesh.rotation.x = -Math.PI / 2 + slopeAngle;
      fabricMesh.scale.set(1, currentExt, 1);
      fabricMesh.position.set(0, rollerY + 0.042 * Math.cos(slopeAngle), rollerZ + 0.042 * Math.sin(slopeAngle));
      terminalBar.position.set(0, terminalY, terminalZ); terminalBar.rotation.x = slopeAngle;
      frontCaps.forEach((cap) => { cap.position.set(cap.userData.dir * (usableWidth / 2 + 0.02), terminalY, terminalZ); cap.rotation.x = slopeAngle; });
      armAssemblies.forEach((arm) => {
        const shoulderPt = new THREE.Vector3(arm.armX, squareBarY - 0.03, squareBarZ + 0.075);
        const wristPt = new THREE.Vector3(arm.armX, terminalY - 0.06, terminalZ - 0.02);
        const delta = new THREE.Vector3().subVectors(wristPt, shoulderPt);
        const pN = new THREE.Vector3(0, Math.cos(slopeAngle), Math.sin(slopeAngle)).normalize();
        const pRight = new THREE.Vector3(1, 0, 0);
        const pForward = new THREE.Vector3().crossVectors(pRight, pN).normalize();
        const deltaForward = delta.dot(pForward);
        const l1 = projection * 0.55; const l2 = projection * 0.55;
        const armDist = Math.max(0.01, Math.sqrt(deltaForward * deltaForward + delta.x * delta.x));
        const reach = Math.max(0.01, Math.min(armDist, l1 + l2 - 0.001));
        const a = (l1 * l1 - l2 * l2 + reach * reach) / (2 * reach);
        const elbowH = Math.sqrt(Math.max(0, l1 * l1 - a * a));
        const dirDir = new THREE.Vector2(delta.x, deltaForward).normalize();
        const perpR = new THREE.Vector2(dirDir.y * arm.foldSign, -dirDir.x * arm.foldSign);
        const elbowLocalX = dirDir.x * a + perpR.x * elbowH;
        const elbowLocalF = dirDir.y * a + perpR.y * elbowH;
        const elbowPt = new THREE.Vector3().copy(shoulderPt)
          .add(pRight.clone().multiplyScalar(elbowLocalX))
          .add(pForward.clone().multiplyScalar(elbowLocalF));
        alignBeam(arm.upperArm, shoulderPt, elbowPt);
        alignBeam(arm.lowerArm, elbowPt, wristPt);
        arm.elbowPin.position.copy(elbowPt);
        const v1 = new THREE.Vector3().subVectors(elbowPt, shoulderPt).normalize();
        const v2 = new THREE.Vector3().subVectors(wristPt, elbowPt).normalize();
        const cross = new THREE.Vector3().crossVectors(v1, v2).normalize();
        if (cross.lengthSq() > 0.01) arm.elbowPin.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), cross);
        else arm.elbowPin.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), pN);
      });
    };
  }
}

// ---------- 8000 EVO (awning_arm_8000evo) — cassetta + doppio braccio ----------
export class Awning8000EvoModel extends AwningBase {
  constructor(m, c) { super(m, c); this.type = 'awning_arm_8000evo'; this.accentMat = new THREE.MeshPhysicalMaterial({ roughness: 0.34, metalness: 0.2, clearcoat: 0.15 }); }
  setFabricColor(c) { super.setFabricColor(c); if (this.accentMat) this.accentMat.color.set(c).multiplyScalar(0.7); }
  _construct(dims, config) {
    this._cfg = config;
    const w = dims.w; const d = dims.d;
    const matStruttura = this.matStruttura; const detailMat = this.detailMat; const darkPinMat = this.darkPinMat; const accentMat = this.accentMat;
    accentMat.color.copy(this.foldMat.color).multiplyScalar(0.7);
    const g = this.group;
    const wallLineZ = -d / 2 + 0.06;
    const projection = Math.max(0.70, d - 0.10);
    const usableWidth = Math.max(0.75, w - 0.12);
    const cassetteHeight = 0.24; const cassetteDepth = 0.28;
    const frontBarDepth = 0.09; const frontBarHeight = 0.11; const armDrop = 0.11;
    const hRear = H + 0.56;
    const rollerAxisY = hRear - cassetteHeight * 0.42;
    const rollerAxisZ = wallLineZ + cassetteDepth * 0.38;
    this._wall(w, hRear, wallLineZ - 0.03);

    const hoodShape = new THREE.Shape();
    hoodShape.moveTo(cassetteDepth * 0.04, cassetteHeight * 0.48);
    hoodShape.bezierCurveTo(-cassetteDepth * 0.22, cassetteHeight * 0.58, -cassetteDepth * 0.85, cassetteHeight * 0.55, -cassetteDepth, cassetteHeight * 0.12);
    hoodShape.bezierCurveTo(-cassetteDepth * 0.96, -cassetteHeight * 0.34, -cassetteDepth * 0.20, -cassetteHeight * 0.42, cassetteDepth * 0.04, -cassetteHeight * 0.20);
    hoodShape.lineTo(cassetteDepth * 0.02, cassetteHeight * 0.10); hoodShape.closePath();
    const hoodMesh = extrudedAlongX(hoodShape, usableWidth + 0.16, matStruttura, 0.002);
    hoodMesh.position.set(0, rollerAxisY - cassetteHeight * 0.22, wallLineZ + cassetteDepth * 0.18); g.add(hoodMesh);

    const backBar = applyShadows(new THREE.Mesh(new THREE.BoxGeometry(usableWidth + 0.18, 0.28, 0.024), detailMat));
    backBar.position.set(0, rollerAxisY - 0.08, wallLineZ - 0.004); g.add(backBar);

    const rollerGeo = new THREE.CylinderGeometry(0.052, 0.052, usableWidth, 24); rollerGeo.rotateZ(Math.PI / 2);
    const roller = applyShadows(new THREE.Mesh(rollerGeo, matStruttura)); roller.position.set(0, rollerAxisY, rollerAxisZ); g.add(roller);
    [-1, 1].forEach((dir) => { const cap = pinDisc(cassetteHeight * 0.32, 0.024, detailMat, 'x'); cap.position.set(dir * (usableWidth / 2 + 0.03), rollerAxisY, rollerAxisZ + 0.01); g.add(cap); });

    const fabricGeo = new THREE.PlaneGeometry(usableWidth, 1, 24, 8); fabricGeo.translate(0, -0.5, 0);
    const fabricMesh = applyShadows(new THREE.Mesh(fabricGeo, this.foldMat)); g.add(fabricMesh);

    const frontBarShape = new THREE.Shape();
    frontBarShape.moveTo(0, frontBarHeight * 0.48);
    frontBarShape.bezierCurveTo(-frontBarDepth * 0.32, frontBarHeight * 0.54, -frontBarDepth, frontBarHeight * 0.44, -frontBarDepth, 0);
    frontBarShape.bezierCurveTo(-frontBarDepth * 0.95, -frontBarHeight * 0.44, -frontBarDepth * 0.18, -frontBarHeight * 0.52, 0, -frontBarHeight * 0.18);
    frontBarShape.closePath();
    const frontBar = extrudedAlongX(frontBarShape, usableWidth + 0.04, matStruttura, 0.0015); g.add(frontBar);
    const frontCaps = [];
    [-1, 1].forEach((dir) => { const cap = pinDisc(frontBarHeight * 0.36, 0.022, detailMat, 'x'); cap.userData.dir = dir; frontCaps.push(cap); g.add(cap); });

    const armXs = w > 5.8 ? [-usableWidth * 0.34, 0, usableWidth * 0.34] : [-usableWidth * 0.30, usableWidth * 0.30];
    const armAssemblies = [];
    armXs.forEach((armX) => {
      const wallPlate = applyShadows(new THREE.Mesh(new THREE.BoxGeometry(0.045, 0.26, 0.10), matStruttura));
      wallPlate.position.set(armX, rollerAxisY - 0.12, wallLineZ + 0.05); g.add(wallPlate);
      const upperA = linkBeam(0.05, 0.08, detailMat); const upperB = linkBeam(0.05, 0.08, detailMat);
      const lowerA = linkBeam(0.045, 0.06, detailMat); const lowerB = linkBeam(0.045, 0.06, detailMat);
      const tieRod = linkBeam(0.022, 0.032, accentMat);
      const shoulderPin = pinDisc(0.032, 0.05, darkPinMat, 'x'); const elbowPin = pinDisc(0.028, 0.06, darkPinMat, 'x');
      const wristPin = pinDisc(0.026, 0.05, darkPinMat, 'x'); const lowerShoulderPin = pinDisc(0.022, 0.04, darkPinMat, 'x'); const lowerWristPin = pinDisc(0.020, 0.04, darkPinMat, 'x');
      g.add(upperA, upperB, lowerA, lowerB, tieRod, shoulderPin, elbowPin, wristPin, lowerShoulderPin, lowerWristPin);
      armAssemblies.push({ armX, upperA, upperB, lowerA, lowerB, tieRod, shoulderPin, elbowPin, wristPin, lowerShoulderPin, lowerWristPin });
    });

    g.userData.updateLayout = (prog) => {
      const openness = 1 - THREE.MathUtils.clamp(prog, 0, 1);
      const slopeAngle = THREE.MathUtils.degToRad(20);
      const minExtension = 0.34;
      const currentExt = minExtension + openness * Math.max(0.12, projection - minExtension);
      const barY = rollerAxisY - Math.sin(slopeAngle) * currentExt;
      const barZ = rollerAxisZ + Math.cos(slopeAngle) * currentExt;
      fabricMesh.rotation.x = -Math.PI / 2 + slopeAngle; fabricMesh.scale.set(1, currentExt, 1); fabricMesh.position.set(0, rollerAxisY, rollerAxisZ);
      frontBar.position.set(0, barY - frontBarHeight * 0.18, barZ + frontBarDepth * 0.10);
      frontCaps.forEach((cap) => cap.position.set(cap.userData.dir * (usableWidth / 2), barY - frontBarHeight * 0.18, barZ + frontBarDepth * 0.10));
      armAssemblies.forEach((arm) => {
        const shoulder = new THREE.Vector3(arm.armX, rollerAxisY - 0.11, wallLineZ + 0.08);
        const frontPivot = new THREE.Vector3(arm.armX, barY - 0.015, barZ - frontBarDepth * 0.16);
        const elbow = solveElbow(shoulder, frontPivot, currentExt * 0.50, currentExt * 0.50);
        const lowerShoulder = new THREE.Vector3(arm.armX, rollerAxisY - 0.20, wallLineZ + 0.07);
        const lowerFront = new THREE.Vector3(arm.armX, barY - armDrop, barZ - frontBarDepth * 0.24);
        const lowerElbow = solveElbow(lowerShoulder, lowerFront, currentExt * 0.48, currentExt * 0.48);
        alignBeam(arm.upperA, shoulder, elbow); alignBeam(arm.upperB, elbow, frontPivot);
        alignBeam(arm.lowerA, lowerShoulder, lowerElbow); alignBeam(arm.lowerB, lowerElbow, lowerFront);
        alignBeam(arm.tieRod, elbow.clone().add(new THREE.Vector3(0, -0.02, -0.015)), lowerElbow.clone().add(new THREE.Vector3(0, 0.015, 0.010)));
        arm.shoulderPin.position.copy(shoulder); arm.elbowPin.position.copy(elbow); arm.wristPin.position.copy(frontPivot);
        arm.lowerShoulderPin.position.copy(lowerShoulder); arm.lowerWristPin.position.copy(lowerFront);
      });
    };
  }
  dispose() { super.dispose(); if (this.accentMat) this.accentMat.dispose(); }
}

// ---------- CAPPOTTINA (awning_cappottina) — K50 a costoloni curvi ----------
export class AwningCappottinaModel extends AwningBase {
  constructor(m, c) { super(m, c); this.type = 'awning_cappottina'; }
  _construct(dims, config) {
    this._cfg = config;
    const w = dims.w; const d = dims.d;
    const matStruttura = this.matStruttura; const darkPinMat = this.darkPinMat;
    const g = this.group;
    const wallLineZ = -d / 2 + 0.05;
    const width = Math.max(0.95, w); const projection = Math.max(0.78, d);
    const shellWidth = width - 0.12; const pivotY = H - projection + 0.08;
    window.ASG_FurnitureActive = false;
    const easeInOutCubic = (t) => { const x = THREE.MathUtils.clamp(t, 0, 1); return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2; };

    const ribPoints = []; const pW = shellWidth; const pD = projection; const rC = 0.14; const curveSteps = 12;
    ribPoints.push(new THREE.Vector3(-pW / 2, 0, 0)); ribPoints.push(new THREE.Vector3(-pW / 2, 0, pD - rC));
    for (let i = 1; i <= curveSteps; i++) { const a = Math.PI - (Math.PI / 2 * i / curveSteps); ribPoints.push(new THREE.Vector3(-pW / 2 + rC + rC * Math.cos(a), 0, pD - rC + rC * Math.sin(a))); }
    ribPoints.push(new THREE.Vector3(pW / 2 - rC, 0, pD));
    for (let i = 1; i <= curveSteps; i++) { const a = Math.PI / 2 - (Math.PI / 2 * i / curveSteps); ribPoints.push(new THREE.Vector3(pW / 2 - rC + rC * Math.cos(a), 0, pD - rC + rC * Math.sin(a))); }
    ribPoints.push(new THREE.Vector3(pW / 2, 0, 0));
    const ribPath = new THREE.CatmullRomCurve3(ribPoints, false, 'chordal', 0.1);
    const numSegments = 36; const profilePtsLocal = ribPath.getPoints(numSegments);

    g.position.set(0, pivotY, wallLineZ);
    const numRibs = 5; const ribMeshes = []; const tubeGeo = new THREE.TubeGeometry(ribPath, 72, 0.015, 8, false);
    for (let i = 0; i < numRibs; i++) { const rib = applyShadows(new THREE.Mesh(tubeGeo, matStruttura)); g.add(rib); ribMeshes.push(rib); }
    [-1, 1].forEach((dir) => {
      const bracketGroup = new THREE.Group();
      const backPlate = applyShadows(new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.14, 0.01), matStruttura)); backPlate.position.set(0, 0, -0.005);
      const sidePlate = applyShadows(new THREE.Mesh(new THREE.BoxGeometry(0.01, 0.08, 0.06), matStruttura)); sidePlate.position.set(dir * 0.015, 0, 0.025);
      bracketGroup.add(backPlate, sidePlate);
      const pin = applyShadows(new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, 0.05, 12), darkPinMat)); pin.rotation.z = Math.PI / 2; bracketGroup.add(pin);
      bracketGroup.position.set(dir * (pW / 2 + 0.025), 0, 0); g.add(bracketGroup);
    });

    const panels = numRibs - 1; const rowsPerPanel = 4; const totalRows = panels * rowsPerPanel + 1;
    const fabricGeo = new THREE.BufferGeometry();
    const positions = new Float32Array(totalRows * (numSegments + 1) * 3);
    const uvs = new Float32Array(totalRows * (numSegments + 1) * 2);
    const indices = [];
    for (let r = 0; r < totalRows - 1; r++) {
      for (let c = 0; c < numSegments; c++) {
        const a = r * (numSegments + 1) + c; const b = a + (numSegments + 1); const dI = a + 1; const cI = b + 1;
        indices.push(a, b, dI); indices.push(b, cI, dI);
      }
    }
    fabricGeo.setIndex(indices);
    fabricGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    fabricGeo.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
    const fabricMesh = applyShadows(new THREE.Mesh(fabricGeo, this.foldMat)); g.add(fabricMesh);

    g.userData.animationSpeed = 0.0016; g.userData.minAnimationSpeed = 0.0008;
    g.userData.updateLayout = (prog) => {
      const openFactor = 1 - easeInOutCubic(THREE.MathUtils.clamp(prog, 0, 1));
      const ribAngles = []; const stackOffset = 0.08;
      for (let i = 0; i < numRibs; i++) {
        const closedAngle = -Math.PI / 2 + i * stackOffset;
        const openAngle = -Math.PI / 2 + (Math.PI / 2) * (i / (numRibs - 1));
        const cur = THREE.MathUtils.lerp(closedAngle, openAngle, openFactor); ribAngles.push(cur); ribMeshes[i].rotation.x = cur;
      }
      const posAttr = fabricMesh.geometry.attributes.position; const uvAttr = fabricMesh.geometry.attributes.uv;
      let vIdx = 0;
      for (let r = 0; r < totalRows; r++) {
        const panelIdx = Math.min(panels - 1, Math.floor(r / rowsPerPanel));
        const alpha = (r % rowsPerPanel) / rowsPerPanel; const isLast = (r === totalRows - 1);
        const ep = isLast ? panels - 1 : panelIdx; const ea = isLast ? 1 : alpha;
        const cur = THREE.MathUtils.lerp(ribAngles[ep], ribAngles[ep + 1], ea);
        const q = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), cur);
        const sag = Math.sin(ea * Math.PI) * 0.02 * (openFactor + 0.1);
        for (let c = 0; c <= numSegments; c++) {
          const pt = profilePtsLocal[c].clone(); pt.z -= sag * (pt.z / pD); if (pt.z < 0) pt.z = 0; pt.applyQuaternion(q);
          posAttr.setXYZ(vIdx, pt.x, pt.y, pt.z); uvAttr.setXY(vIdx, (c / numSegments) * 2, (r / (totalRows - 1)) * 4); vIdx++;
        }
      }
      posAttr.needsUpdate = true; uvAttr.needsUpdate = true; fabricMesh.geometry.computeVertexNormals();
    };
  }
}

// ---------- MOLLA / FALL (awning_molla, awning_fall) ----------
export class AwningMollaModel extends AwningBase {
  constructor(m, c) { super(m, c); this.type = 'awning_molla'; }
  _construct(dims, config) {
    this._cfg = config;
    const w = dims.w; const d = dims.d;
    const matStruttura = this.matStruttura;
    const g = this.group;
    const hRear = H + 0.4;
    const wallLineZ = -d / 2 + 0.05;
    const usableWidth = Math.max(0.5, w - 0.12);
    const profileWidth = w + 0.10;
    const fallH = Math.max(1, H);
    const armLen = 0.50;
    const hoodHeight = 0.14; const hoodDepth = 0.14;
    const rollerAxisY = hRear - 0.08; const rollerAxisZ = wallLineZ + 0.07;
    this._wall(w, hRear, wallLineZ - 0.03);

    const hoodShape = new THREE.Shape();
    hoodShape.moveTo(0, hoodHeight); hoodShape.lineTo(-hoodDepth, hoodHeight); hoodShape.lineTo(-hoodDepth, 0); hoodShape.lineTo(0, 0); hoodShape.closePath();
    const hoodGeo = new THREE.ExtrudeGeometry(hoodShape, { depth: profileWidth, bevelEnabled: true, bevelSegments: 2, steps: 1, bevelSize: 0.002, bevelThickness: 0.002 });
    hoodGeo.translate(0, 0, -profileWidth / 2); hoodGeo.rotateY(Math.PI / 2);
    const hoodMesh = applyShadows(new THREE.Mesh(hoodGeo, matStruttura)); hoodMesh.position.set(0, rollerAxisY - hoodHeight / 2, rollerAxisZ + hoodDepth / 2); g.add(hoodMesh);

    const teloPlaneGeo = new THREE.PlaneGeometry(usableWidth || w, 1, 16, 1); teloPlaneGeo.translate(0, -0.5, 0);
    const teloMesh = applyShadows(new THREE.Mesh(teloPlaneGeo, this.foldMat)); teloMesh.position.set(0, rollerAxisY, rollerAxisZ); g.add(teloMesh);

    const armPivotY = rollerAxisY - fallH + 0.15; const armPivotZ = wallLineZ + 0.03;
    const armXs = [usableWidth / 2 - 0.05, -usableWidth / 2 + 0.05]; const arms = [];
    armXs.forEach((x) => {
      const wallBracket = applyShadows(new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.12, 0.08), matStruttura)); wallBracket.position.set(x, armPivotY, armPivotZ - 0.02); g.add(wallBracket);
      const braccetto = new THREE.Group();
      const beamGeo = new THREE.BoxGeometry(0.016, armLen, 0.03); beamGeo.translate(0, armLen / 2, 0);
      const beam = applyShadows(new THREE.Mesh(beamGeo, matStruttura)); braccetto.add(beam); braccetto.position.set(x, armPivotY, armPivotZ); g.add(braccetto); arms.push(braccetto);
    });

    const terminalShape = new THREE.Shape();
    terminalShape.arc(0, 0, 0.02, 0, Math.PI); terminalShape.lineTo(-0.02, -0.04); terminalShape.lineTo(0.02, -0.04); terminalShape.closePath();
    const terminalGeo = new THREE.ExtrudeGeometry(terminalShape, { depth: usableWidth + 0.04, bevelEnabled: true, bevelSegments: 2, steps: 1, bevelSize: 0.002, bevelThickness: 0.002 });
    terminalGeo.translate(0, 0, -(usableWidth + 0.04) / 2); terminalGeo.rotateY(Math.PI / 2);
    const terminal = applyShadows(new THREE.Mesh(terminalGeo, matStruttura)); g.add(terminal);

    g.userData.updateLayout = (prog) => {
      const extProg = 1 - THREE.MathUtils.clamp(prog, 0, 1);
      const maxAngle = Math.PI - 0.1; const minAngle = 0.1;
      const angle = minAngle + extProg * (maxAngle - minAngle);
      arms.forEach((a) => { a.rotation.x = angle; });
      const termY = armPivotY + Math.cos(angle) * armLen; const termZ = armPivotZ + Math.sin(angle) * armLen;
      terminal.position.set(0, termY, termZ);
      const dz = termZ - rollerAxisZ; const dy = rollerAxisY - termY; const dist = Math.sqrt(dz * dz + dy * dy);
      teloMesh.scale.set(1, Math.max(0.01, dist), 1); teloMesh.rotation.x = Math.atan2(dz, dy);
    };
  }
}
